import { z } from 'zod';
import { autonomy, isoDateTime, jsonObject, runStatus, uuid } from './primitives';
import {
  agentKind,
  agentVersionConfigBase,
  autonomyPosture,
  DEFAULT_AUTONOMY_POSTURE,
  DEFAULT_MODEL,
  DEFAULT_RUN_MODE,
  modelId,
  refineAgentVersionConfig,
  refineRunModeTriggers,
  runMode,
} from './episode';

/**
 * Agent config wire contracts (Master Spec §6.2, §14). Promoted from
 * `apps/server/src/agents/schemas.ts` now that the web client (WP-10) consumes them
 * (that file explicitly deferred promotion to WP-10). The publish/version-config
 * shape is the shared `agentVersionConfig` EXTENDED with the authoritative
 * `triggers` list the platform projects into `agent_triggers`/`schedules`.
 *
 * The server keeps parsing with its own `versionConfig` schema (identical, built by
 * `.extend` on the same base); this module is the single vocabulary the client and
 * server share so the builder form and the API agree by construction (§8.2).
 */

/** Matches a zero-padded 24h time-of-day, `HH:MM` (00:00–23:59). */
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Active-hours window on a schedule trigger (v1.10.1): the schedule only fires
 * WITHIN this window, interpreted in the trigger's `timezone`. `start`/`end` are
 * `HH:MM` times of day; `days` (optional) restricts which weekdays fire (0=Sunday…
 * 6=Saturday) — absent means every day. v1 supports SAME-DAY windows only: `start`
 * must be strictly before `end` (a cross-midnight window like 22:00–06:00 is out of
 * scope and rejected). Zero-padded `HH:MM` sorts lexicographically, so a string
 * compare is a correct time-of-day compare.
 */
export const scheduleActiveWindow = z
  .object({
    start: z.string().regex(HHMM, 'start must be a 24h time of day, HH:MM'),
    end: z.string().regex(HHMM, 'end must be a 24h time of day, HH:MM'),
    days: z.array(z.number().int().min(0).max(6)).min(1).optional(),
  })
  .refine((w) => w.start < w.end, {
    message:
      'active-hours start must be before end (same-day windows only; a cross-midnight window like 22:00–06:00 is not supported)',
    path: ['end'],
  });
export type ScheduleActiveWindow = z.infer<typeof scheduleActiveWindow>;

/** One trigger in a version's config (§6.2). Discriminated by `kind`. */
export const triggerConfig = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('event'),
    eventType: z.string().min(1),
    connectionId: uuid.optional(),
  }),
  z.object({
    kind: z.literal('schedule'),
    cron: z.string().min(1),
    timezone: z.string().min(1).default('UTC'),
    // Optional active-hours window (v1.10.1): outside it the schedule does not fire.
    activeWindow: scheduleActiveWindow.optional(),
  }),
  z.object({ kind: z.literal('manual') }),
]);
export type TriggerConfig = z.infer<typeof triggerConfig>;

/**
 * The full immutable version config stored in `agent_versions.config` (§6.2): the
 * shared version-config shape PLUS the authoritative `triggers` list. Built by
 * `.extend`ing the base object (so `triggers` composes in) and re-applying the
 * `kind`/`research` cross-field rules (§13.1) via `.superRefine`.
 */
export const versionConfig = agentVersionConfigBase
  .extend({
    // Normalize the published version's kind to `inbox` when omitted (§13.1).
    kind: agentKind.default('inbox'),
    // Normalize the published version's model to the cheaper default when omitted
    // (#89) — existing versions with no model publish/behave as `DEFAULT_MODEL`.
    model: modelId.default(DEFAULT_MODEL),
    // Normalize the published version's run mode to `static` when omitted (§13.2,
    // #147) — every existing version publishes/behaves as `static`, unchanged.
    runMode: runMode.default(DEFAULT_RUN_MODE),
    // Normalize the autonomy posture to `approval_only` when omitted (§13.3, v1.6) —
    // every existing/non-task version publishes/behaves as `approval_only` (the safe
    // no-op default). A loosened posture is validated against `kind` in the refine.
    autonomyPosture: autonomyPosture.default(DEFAULT_AUTONOMY_POSTURE),
    triggers: z.array(triggerConfig).default([]),
  })
  .superRefine((cfg, ctx) => {
    refineAgentVersionConfig(cfg, ctx);
    // §13.2/#147: `templated` + `schedule` is rejected here (triggers are visible).
    refineRunModeTriggers(cfg, ctx);
  });
export type VersionConfig = z.infer<typeof versionConfig>;

/** Per-tool grant on a version (name + autonomy). Re-export for the builder. */
export const toolGrant = z.object({ tool: z.string().min(1), autonomy });
export type ToolGrant = z.infer<typeof toolGrant>;

// ── Request bodies (§14 agent builder) ──────────────────────────────────────

export const createAgentRequest = z.object({
  name: z.string().trim().min(1).max(200),
  /** Agent kind (§13.1). Defaults to `inbox`; `research` selects the research agent. */
  kind: agentKind.default('inbox'),
});
export type CreateAgentRequest = z.infer<typeof createAgentRequest>;

/**
 * PATCH /api/workspaces/:workspaceId/agents/:agentId — rename an agent (#294). The name
 * is the ONE mutable field on an agent (its `kind`, published versions and config are
 * immutable); reuses the create-name validation verbatim (trimmed, 1–200 chars).
 */
export const renameAgentRequest = z.object({
  name: z.string().trim().min(1).max(200),
});
export type RenameAgentRequest = z.infer<typeof renameAgentRequest>;

export const publishVersionRequest = z.object({ config: versionConfig });
export type PublishVersionRequest = z.infer<typeof publishVersionRequest>;

// Status transitions on an agent (§6.2). `archive`/`unarchive` (v1.11.0, #215) are the
// reversible hide/show pair: archiving sets `archived` (hidden from the default agent +
// runs lists, cost still counted); unarchiving returns the agent to `active`.
export const statusTransition = z.enum([
  'pause',
  'disable',
  'resume',
  'archive',
  'unarchive',
]);
export type StatusTransition = z.infer<typeof statusTransition>;

export const updateStatusRequest = z.object({ action: statusTransition });
export type UpdateStatusRequest = z.infer<typeof updateStatusRequest>;

// ── Response views (§14) ─────────────────────────────────────────────────────

// Agent status (§6.2). `archived` (v1.11.0, #215) is a reversible state that hides the
// agent + its runs from the default lists while its runs' cost stays in the totals.
export const agentStatus = z.enum(['active', 'paused', 'disabled', 'archived']);
export type AgentStatus = z.infer<typeof agentStatus>;

/** An agent row as returned by the agents API. */
export const agentView = z.object({
  id: uuid,
  workspaceId: uuid,
  name: z.string(),
  kind: agentKind,
  status: agentStatus,
  currentVersionId: uuid.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  /**
   * The next expected fire time of this agent's schedule (v1.13.5): the
   * scheduler-maintained `schedules.next_run_at` (timezone + active-window aware),
   * as an ISO datetime. `null` when the agent has no active schedule — a manual/
   * inbox/event-only agent, a paused/disabled/archived agent, or one whose schedule
   * is disabled. When an agent has several schedule triggers it is the earliest.
   */
  nextRunAt: isoDateTime.nullable(),
  /**
   * Run-health derived from this agent's MOST RECENT run (by `created_at`, #275) —
   * additive & back-compat. The agents list turns these into a health indicator:
   * a FAILED/BLOCKED/LIMIT_EXCEEDED last run flags the agent, a COMPLETED/
   * LIMIT_REACHED one confirms it healthy. `null` when the agent has never run.
   *
   *   - `lastRunStatus`      — the last run's lifecycle status, or null (no runs).
   *   - `lastRunAt`          — when that run was created (ISO datetime), or null.
   *   - `lastRunEndedReason` — the operator-visible reason a blocked/failed last run
   *     ended/parked (e.g. an I8 ambiguity note); null for a healthy/running run or
   *     when no reason was recorded.
   */
  lastRunStatus: runStatus.nullable(),
  lastRunAt: isoDateTime.nullable(),
  lastRunEndedReason: z.string().nullable(),
});
export type AgentView = z.infer<typeof agentView>;

/** A published immutable version row (config is opaque JSONB to the wire). */
export const agentVersionView = z.object({
  id: uuid,
  agentId: uuid,
  versionNo: z.number().int().positive(),
  config: jsonObject,
  /**
   * When this version was published (ISO datetime). Maps to the Prisma
   * `AgentVersion.publishedAt` column (`published_at`) — the versions endpoint returns
   * raw Prisma rows, so the wire field is `publishedAt`, not `createdAt`.
   */
  publishedAt: isoDateTime,
});
export type AgentVersionView = z.infer<typeof agentVersionView>;
